/**
 * ユーザー活動分析ページ
 *
 * /admin/user-activity からアクセス
 * サイト管理者がユーザーの活動状況を分析するための画面
 */

import { useState, useMemo } from 'react';
import {
  Card,
  Col,
  DatePicker,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  Alert,
  Empty,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  BarChartOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  EyeOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { trpc } from '../../lib/api';
import { useHashTab } from '../../hooks/useHashTab';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// タブキー定義
const TAB_KEYS = ['overview', 'companies', 'users', 'peak'] as const;
type TabKey = (typeof TAB_KEYS)[number];

// カラーパレット
const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16'];

type CompanyStatsRow = {
  companyId: string;
  companyName: string | null;
  totalSessions: number;
  totalPageViews: number;
  totalActions: number;
  uniqueUsers: number;
};

type UserEngagementRow = {
  userId: string;
  userName: string | null;
  email: string;
  companyName: string | null;
  totalSessions: number;
  totalPageViews: number;
  totalActions: number;
  lastActivityAt: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeCompanyStatsRow = (value: unknown): CompanyStatsRow | null => {
  if (!isRecord(value) || typeof value.companyId !== 'string') {
    return null;
  }

  return {
    companyId: value.companyId,
    companyName: typeof value.companyName === 'string' ? value.companyName : null,
    totalSessions: typeof value.totalSessions === 'number' ? value.totalSessions : 0,
    totalPageViews: typeof value.totalPageViews === 'number' ? value.totalPageViews : 0,
    totalActions: typeof value.totalActions === 'number' ? value.totalActions : 0,
    uniqueUsers: typeof value.uniqueUsers === 'number' ? value.uniqueUsers : 0,
  };
};

const normalizeUserEngagementRow = (value: unknown): UserEngagementRow | null => {
  if (!isRecord(value) || typeof value.userId !== 'string') {
    return null;
  }

  return {
    userId: value.userId,
    userName: typeof value.userName === 'string' ? value.userName : null,
    email: typeof value.email === 'string' ? value.email : '',
    companyName: typeof value.companyName === 'string' ? value.companyName : null,
    totalSessions: typeof value.totalSessions === 'number' ? value.totalSessions : 0,
    totalPageViews: typeof value.totalPageViews === 'number' ? value.totalPageViews : 0,
    totalActions: typeof value.totalActions === 'number' ? value.totalActions : 0,
    lastActivityAt: typeof value.lastActivityAt === 'string' ? value.lastActivityAt : null,
  };
};

/**
 * 時間を「HH:00」形式にフォーマット
 */
const formatHour = (hour: number): string => {
  return `${hour.toString().padStart(2, '0')}:00`;
};

export function UserActivityPage() {
  // タブ状態
  const [activeTab, setActiveTab] = useHashTab<TabKey>('overview', TAB_KEYS);

  // 日付範囲（デフォルト: 過去7日）
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);

  const startDate = dateRange[0].format('YYYY-MM-DD');
  const endDate = dateRange[1].format('YYYY-MM-DD');

  // 概要統計
  const { data: overview, isLoading: isLoadingOverview } = trpc.adminUserActivity.getOverview.useQuery(
    { startDate, endDate },
    { enabled: activeTab === 'overview' }
  );

  // 日別統計
  const { data: dailyStats, isLoading: isLoadingDaily } = trpc.adminUserActivity.getDailyStats.useQuery(
    { startDate, endDate },
    { enabled: activeTab === 'overview' }
  );

  // ピーク分析
  const { data: peakAnalysis, isLoading: isLoadingPeak } = trpc.adminUserActivity.getPeakAnalysis.useQuery(
    { startDate, endDate },
    { enabled: activeTab === 'peak' }
  );

  // 会社別統計
  const { data: companyStats, isLoading: isLoadingCompanies } = trpc.adminUserActivity.getCompanyStats.useQuery(
    { startDate, endDate, page: 1, pageSize: 50 },
    { enabled: activeTab === 'companies' }
  );

  // ユーザーエンゲージメント
  const { data: userEngagement, isLoading: isLoadingUsers } = trpc.adminUserActivity.getUserEngagement.useQuery(
    { startDate, endDate, page: 1, pageSize: 50 },
    { enabled: activeTab === 'users' }
  );

  // 時間帯別チャートデータ
  const hourlyChartData = useMemo(() => {
    if (!overview?.hourlyDistribution) return [];
    return Object.entries(overview.hourlyDistribution).map(([hour, views]) => ({
      hour: formatHour(Number(hour)),
      pageViews: views,
    }));
  }, [overview?.hourlyDistribution]);

  // 日別チャートデータ
  const dailyChartData = useMemo(() => {
    if (!dailyStats?.stats) return [];
    return dailyStats.stats.map((stat) => ({
      date: dayjs(stat.statDate).format('MM/DD'),
      pageViews: stat.totalPageViews,
      sessions: stat.totalSessions,
      actions: stat.totalActions,
    }));
  }, [dailyStats?.stats]);

  // アクションカテゴリ別チャートデータ
  const actionChartData = useMemo(() => {
    if (!overview?.actionBreakdown) return [];
    const categoryLabels: Record<string, string> = {
      create: '作成',
      update: '更新',
      delete: '削除',
      export: 'エクスポート',
      import: 'インポート',
      download: 'ダウンロード',
      search: '検索',
      filter: 'フィルター',
      navigate: 'ナビゲーション',
      other: 'その他',
    };
    return Object.entries(overview.actionBreakdown)
      .map(([category, count]) => ({
        name: categoryLabels[category] || category,
        value: Number(count),
      }))
      .sort((a, b) => b.value - a.value);
  }, [overview?.actionBreakdown]);

  // ピーク時間帯チャートデータ
  const peakHourChartData = useMemo(() => {
    if (!peakAnalysis?.peakHours) return [];
    return peakAnalysis.peakHours.map((h) => ({
      hour: formatHour(h.hour),
      sessions: h.avgSessions,
      pageViews: h.avgPageViews,
    }));
  }, [peakAnalysis?.peakHours]);

  // 曜日別チャートデータ
  const peakDayChartData = useMemo(() => {
    if (!peakAnalysis?.peakDays) return [];
    return peakAnalysis.peakDays.map((d) => ({
      day: d.dayName,
      sessions: d.avgSessions,
      pageViews: d.avgPageViews,
    }));
  }, [peakAnalysis?.peakDays]);

  const companyRows = useMemo(
    () => (companyStats?.companies ?? []).flatMap((company) => {
      const normalized = normalizeCompanyStatsRow(company);
      return normalized ? [normalized] : [];
    }),
    [companyStats?.companies]
  );

  const userRows = useMemo(
    () => (userEngagement?.users ?? []).flatMap((user) => {
      const normalized = normalizeUserEngagementRow(user);
      return normalized ? [normalized] : [];
    }),
    [userEngagement?.users]
  );

  // 会社別テーブルカラム
  const companyColumns: ColumnsType<CompanyStatsRow> = [
    {
      title: '会社名',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (name: string | null) => name || '(名称なし)',
    },
    {
      title: 'セッション数',
      dataIndex: 'totalSessions',
      key: 'totalSessions',
      sorter: (a, b) => a.totalSessions - b.totalSessions,
    },
    {
      title: 'ページビュー',
      dataIndex: 'totalPageViews',
      key: 'totalPageViews',
      sorter: (a, b) => a.totalPageViews - b.totalPageViews,
    },
    {
      title: 'アクション数',
      dataIndex: 'totalActions',
      key: 'totalActions',
      sorter: (a, b) => a.totalActions - b.totalActions,
    },
    {
      title: 'ユニークユーザー',
      dataIndex: 'uniqueUsers',
      key: 'uniqueUsers',
    },
  ];

  // ユーザー別テーブルカラム
  const userColumns: ColumnsType<UserEngagementRow> = [
    {
      title: 'ユーザー名',
      dataIndex: 'userName',
      key: 'userName',
      render: (name: string | null, record) => name || record.email,
    },
    {
      title: '会社',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (name: string | null) => name || '-',
    },
    {
      title: 'セッション数',
      dataIndex: 'totalSessions',
      key: 'totalSessions',
      sorter: (a, b) => a.totalSessions - b.totalSessions,
    },
    {
      title: 'ページビュー',
      dataIndex: 'totalPageViews',
      key: 'totalPageViews',
      sorter: (a, b) => a.totalPageViews - b.totalPageViews,
    },
    {
      title: 'アクション数',
      dataIndex: 'totalActions',
      key: 'totalActions',
    },
    {
      title: '最終アクティビティ',
      dataIndex: 'lastActivityAt',
      key: 'lastActivityAt',
      render: (date: string | null) => date ? dayjs(date).format('YYYY/MM/DD HH:mm') : '-',
    },
  ];

  // タブの内容
  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <BarChartOutlined /> 概要
        </span>
      ),
      children: (
        <Spin spinning={isLoadingOverview || isLoadingDaily}>
          {overview ? (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* サマリーカード */}
              <Row gutter={[16, 16]}>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="総セッション数"
                      value={overview.totalSessions}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="総ページビュー"
                      value={overview.totalPageViews}
                      prefix={<EyeOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="総アクション数"
                      value={overview.totalActions}
                      prefix={<ThunderboltOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="ユニークユーザー"
                      value={overview.uniqueUsers}
                      prefix={<TeamOutlined />}
                    />
                  </Card>
                </Col>
              </Row>

              {/* 日別推移グラフ */}
              <Card title="日別推移">
                {dailyChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="pageViews" stroke="#1890ff" name="ページビュー" />
                      <Line yAxisId="right" type="monotone" dataKey="sessions" stroke="#52c41a" name="セッション" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty description="データがありません" />
                )}
              </Card>

              <Row gutter={[16, 16]}>
                {/* 時間帯別分布 */}
                <Col xs={24} lg={12}>
                  <Card title="時間帯別ページビュー">
                    {hourlyChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={hourlyChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hour" />
                          <YAxis />
                          <RechartsTooltip />
                          <Bar dataKey="pageViews" fill="#1890ff" name="ページビュー" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="データがありません" />
                    )}
                  </Card>
                </Col>

                {/* アクションカテゴリ別 */}
                <Col xs={24} lg={12}>
                  <Card title="アクションカテゴリ別">
                    {actionChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={actionChartData}
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {actionChartData.map((_, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="データがありません" />
                    )}
                  </Card>
                </Col>
              </Row>

              {/* トップページ */}
              <Card title="よく閲覧されるページ">
                {overview.topPages && overview.topPages.length > 0 ? (
                  <Table
                    dataSource={overview.topPages}
                    columns={[
                      { title: 'パス', dataIndex: 'path', key: 'path' },
                      { title: 'タイトル', dataIndex: 'title', key: 'title', render: (t: string | null) => t || '-' },
                      { title: '閲覧数', dataIndex: 'views', key: 'views' },
                    ]}
                    rowKey="path"
                    pagination={false}
                    size="small"
                  />
                ) : (
                  <Empty description="データがありません" />
                )}
              </Card>
            </Space>
          ) : (
            <Empty description="データがありません" />
          )}
        </Spin>
      ),
    },
    {
      key: 'companies',
      label: (
        <span>
          <TeamOutlined /> 会社別
        </span>
      ),
      children: (
        <Spin spinning={isLoadingCompanies}>
          <Card title="会社別統計">
            {companyStats?.companies && companyStats.companies.length > 0 ? (
              <Table
                dataSource={companyRows}
                columns={companyColumns}
                rowKey="companyId"
                pagination={{ pageSize: 20 }}
              />
            ) : (
              <Empty description="データがありません" />
            )}
          </Card>
        </Spin>
      ),
    },
    {
      key: 'users',
      label: (
        <span>
          <TeamOutlined /> ユーザー別
        </span>
      ),
      children: (
        <Spin spinning={isLoadingUsers}>
          <Card title="ユーザーエンゲージメント">
            {userEngagement?.users && userEngagement.users.length > 0 ? (
              <Table
                dataSource={userRows}
                columns={userColumns}
                rowKey="userId"
                pagination={{ pageSize: 20 }}
              />
            ) : (
              <Empty description="データがありません" />
            )}
          </Card>
        </Spin>
      ),
    },
    {
      key: 'peak',
      label: (
        <span>
          <ClockCircleOutlined /> ピーク分析
        </span>
      ),
      children: (
        <Spin spinning={isLoadingPeak}>
          {peakAnalysis ? (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* 推奨メンテナンス時間帯 */}
              <Card title="推奨メンテナンス時間帯">
                <Alert
                  type="info"
                  message="以下の時間帯はユーザーアクティビティが低いため、メンテナンスに適しています"
                  style={{ marginBottom: 16 }}
                />
                <Space wrap>
                  {peakAnalysis.recommendedMaintenanceWindows.map((window, index) => (
                    <Tag color="blue" key={index}>
                      {formatHour(window.startHour)} - {formatHour(window.endHour)}
                      （平均 {window.avgSessions.toFixed(1)} セッション）
                    </Tag>
                  ))}
                </Space>
              </Card>

              <Row gutter={[16, 16]}>
                {/* ピーク時間帯 */}
                <Col xs={24} lg={12}>
                  <Card title="時間帯別アクティビティ（平均）">
                    {peakHourChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={peakHourChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="hour" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="sessions" fill="#1890ff" name="平均セッション" />
                          <Bar dataKey="pageViews" fill="#52c41a" name="平均ページビュー" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="データがありません" />
                    )}
                  </Card>
                </Col>

                {/* 曜日別 */}
                <Col xs={24} lg={12}>
                  <Card title="曜日別アクティビティ（平均）">
                    {peakDayChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={peakDayChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="day" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="sessions" fill="#1890ff" name="平均セッション" />
                          <Bar dataKey="pageViews" fill="#52c41a" name="平均ページビュー" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Empty description="データがありません" />
                    )}
                  </Card>
                </Col>
              </Row>
            </Space>
          ) : (
            <Empty description="データがありません" />
          )}
        </Spin>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* ヘッダー */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            ユーザー活動分析
          </Title>
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              allowClear={false}
            />
          </Space>
        </div>

        <Text type="secondary">
          期間: {startDate} 〜 {endDate}
        </Text>

        {/* タブ */}
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TabKey)}
          items={tabItems}
        />
      </Space>
    </div>
  );
}
